import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BookOpen, Sparkles, RefreshCw, Pencil, Trash2, Eye, EyeOff, ExternalLink, AlertTriangle, RotateCcw, Loader2, Wand2 } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { GuideEditDialog } from "./GuideEditDialog";

const STATUS_STYLES = {
  draft: "bg-[#F6F1E7] text-[#8A6D2F] border-[#D4B36A]/50",
  published: "bg-[#EAF2EC] text-[#2D6A4F] border-[#2D6A4F]/30",
};

function Stat({ label, value, tone = "", testid }) {
  return (
    <div className="border border-[#DDD7CF] rounded-lg bg-white p-5" data-testid={testid}>
      <p className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#6E675F]">{label}</p>
      <p className={`text-2xl font-medium mt-1.5 ${tone || "text-[#1A1A1A]"}`}>{value}</p>
    </div>
  );
}

function Progress({ progress }) {
  if (!progress?.started_at) return null;
  const pct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;
  const outcomes = progress.results || [];
  return (
    <div className="border border-[#8C7B6B]/40 rounded-lg bg-white p-5 mb-8" data-testid="guides-progress">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm font-semibold text-[#1A1A1A] flex items-center gap-2">
          {progress.running ? <Loader2 className="w-4 h-4 animate-spin text-[#8C7B6B]" /> : <Sparkles className="w-4 h-4 text-[#2D6A4F]" />}
          {progress.running ? `Generating ${progress.current}…` : "Last generation run finished"}
        </p>
        <span className="text-xs text-[#6E675F]" data-testid="guides-progress-count">{progress.done} / {progress.total}</span>
      </div>
      <div className="h-1.5 bg-[#EFEBE6] rounded-none mt-3 overflow-hidden">
        <div className="h-full bg-[#8C7B6B] transition-[width] duration-500" style={{ width: `${pct}%` }} />
      </div>
      {outcomes.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-3">
          {outcomes.map((r) => (
            <span key={r.peptide} title={r.error || ""} className={`text-[10px] font-semibold rounded-none px-2 py-0.5 border ${r.outcome === "published" ? STATUS_STYLES.published : r.outcome === "draft" ? STATUS_STYLES.draft : r.outcome === "error" ? "bg-[#FBEAEA] text-[#B42318] border-[#B42318]/30" : "bg-[#F7F5F2] text-[#6E675F] border-[#DDD7CF]"}`}>
              {r.peptide} · {r.outcome}{r.corrected ? " · self-corrected" : ""}{r.flags ? ` (${r.flags} flags)` : ""}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function FlagList({ guide }) {
  const dosing = guide.dosing_flags || [];
  const sources = guide.source_flags || [];
  if (!dosing.length && !sources.length) return <p className="text-xs text-[#6E675F] px-5 py-3">No flags on this guide.</p>;
  return (
    <div className="px-5 py-4 space-y-2 text-xs" data-testid={`guide-flags-${guide.slug}`}>
      {dosing.map((f, i) => (
        <p key={`d-${i}`} className="text-[#7C3A0B]"><span className="font-mono text-[#6E675F]">{f.field}</span> — “…{f.context}…” <strong className="bg-[#FEF3E2] px-1 rounded">{f.match}</strong></p>
      ))}
      {sources.map((s, i) => <p key={`s-${i}`} className="text-[#7C3A0B]">Unrecognised source: <span className="break-all">{s}</span></p>)}
    </div>
  );
}

export const AdminGuides = ({ onChanged }) => {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/guides").then(({ data: d }) => setData(d)).catch(() => toast.error("Failed to load guides"));
  }, []);

  useEffect(() => { load(); }, [load]);

  const running = !!data?.progress?.running;
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(load, 4000);
    return () => { clearInterval(t); onChanged?.(); };
  }, [running, load, onChanged]);

  const act = async (key, fn, ok) => {
    setBusy(key);
    try {
      await fn();
      if (ok) toast.success(ok);
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally {
      setBusy(null);
    }
  };

  const generate = (body, label) => act("generate", () => api.post("/admin/guides/generate", body), label);

  if (!data) {
    return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#8C7B6B] border-t-transparent rounded-full animate-spin" /></div>;
  }

  const { guides, counts, missing, progress } = data;
  const visible = filter === "all" ? guides : guides.filter((g) => g.status === filter);

  return (
    <div data-testid="admin-guides-section">
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#6E675F] flex items-center gap-2"><BookOpen className="w-4 h-4" /> Peptide Research Guides</p>
        <div className="flex items-center gap-3">
          <button onClick={load} data-testid="guides-refresh-button" className="text-xs text-[#8C7B6B] font-semibold inline-flex items-center gap-1 hover:text-[#1A1A1A] transition-colors"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
          <Button
            data-testid="guides-generate-missing"
            onClick={() => generate({ all_missing: true }, `Generating ${missing.length} guide(s) in the background`)}
            disabled={running || busy === "generate" || missing.length === 0}
            className="bg-[#8C7B6B] text-white hover:bg-[#75665A]"
          >
            <Sparkles className="w-4 h-4 mr-1.5" /> Generate missing ({missing.length})
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <Stat label="Published" value={counts.published} tone="text-[#2D6A4F]" testid="guides-stat-published" />
        <Stat label="Drafts held for review" value={counts.draft} tone={counts.draft ? "text-[#8A6D2F]" : ""} testid="guides-stat-draft" />
        <Stat label="Peptides without a guide" value={missing.length} testid="guides-stat-missing" />
      </div>

      <p className="text-xs text-[#6E675F] mb-6 max-w-3xl">
        Guides are written by Claude with live web search from PubMed, ClinicalTrials.gov and journals, then screened automatically. Anything that reads like dosing, administration or protocol language — or cites a non-academic source — gets <strong>one automatic self-correction pass</strong> (flagged passages are rewritten, unrecognised sources dropped) and is re-screened. Guides that still trip the screen are <strong>held as drafts</strong> for you to edit and publish manually. Clean guides publish immediately.
      </p>

      <Progress progress={progress} />

      {missing.length > 0 && (
        <div className="border border-[#DDD7CF] rounded-lg bg-white p-5 mb-8" data-testid="guides-missing">
          <p className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#6E675F] mb-3">No guide yet</p>
          <div className="flex flex-wrap gap-2">
            {missing.map((m) => (
              <button
                key={m.peptide}
                data-testid={`guide-generate-one-${m.peptide}`}
                disabled={running}
                onClick={() => generate({ peptides: [m.peptide] }, `Generating ${m.peptide}…`)}
                className="text-xs border border-[#DDD7CF] rounded-none px-3 py-1.5 hover:border-[#8C7B6B] hover:bg-[#F7F5F2] transition-colors disabled:opacity-50 inline-flex items-center gap-1.5"
              >
                <Sparkles className="w-3 h-3 text-[#8C7B6B]" /> {m.peptide}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-5">
        {[["all", `All (${guides.length})`], ["published", `Published (${counts.published})`], ["draft", `Drafts (${counts.draft})`]].map(([k, label]) => (
          <button key={k} data-testid={`guides-filter-${k}`} onClick={() => setFilter(k)} className={`text-xs tracking-[0.1em] uppercase font-semibold px-4 py-2 rounded-none border transition-colors ${filter === k ? "bg-[#1A1A1A] text-white border-[#1A1A1A]" : "text-[#6E675F] border-[#DDD7CF] hover:border-[#8C7B6B]"}`}>{label}</button>
        ))}
      </div>

      {visible.length === 0 ? (
        <p className="text-sm text-[#6E675F] py-10 text-center border border-[#DDD7CF] rounded-lg bg-white" data-testid="guides-empty">
          {guides.length === 0 ? "No guides yet — click “Generate missing” to write one for every peptide in the catalog." : `No ${filter} guides.`}
        </p>
      ) : (
        <div className="border border-[#DDD7CF] rounded-lg bg-white divide-y divide-[#EFEBE6]" data-testid="guides-list">
          {visible.map((g) => {
            const flags = (g.dosing_flags?.length || 0) + (g.source_flags?.length || 0);
            return (
              <div key={g.id} data-testid={`guide-row-${g.slug}`}>
                <div className="flex items-center gap-4 px-5 py-4 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-[#1A1A1A] flex items-center gap-2 flex-wrap">
                      {g.peptide}
                      <span className={`inline-flex text-[10px] font-bold uppercase tracking-wide border rounded-none px-2 py-0.5 ${STATUS_STYLES[g.status]}`} data-testid={`guide-status-${g.slug}`}>{g.status}</span>
                      {flags > 0 && (
                        <button onClick={() => setExpanded((id) => (id === g.id ? null : g.id))} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-[#B45309] bg-[#FEF3E2] border border-[#B45309]/30 rounded-none px-2 py-0.5 hover:bg-[#FBE5C8]" data-testid={`guide-flags-toggle-${g.slug}`}>
                          <AlertTriangle className="w-3 h-3" /> {flags} flag{flags === 1 ? "" : "s"}
                        </button>
                      )}
                      {g.edited && <span className="text-[10px] text-[#6E675F] uppercase tracking-wide">edited</span>}
                      {g.correction && (
                        <span
                          data-testid={`guide-corrected-${g.slug}`}
                          title={`Self-correction: ${g.correction.rewritten_fields?.length || 0} passage(s) rewritten, ${g.correction.dropped_sources?.length || 0} source(s) dropped${g.correction.error ? ` — ${g.correction.error}` : ""}`}
                          className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide rounded-none px-2 py-0.5 border ${g.correction.resolved ? "text-[#2D6A4F] bg-[#EAF2EC] border-[#2D6A4F]/30" : "text-[#6E675F] bg-[#F7F5F2] border-[#DDD7CF]"}`}
                        >
                          <Sparkles className="w-3 h-3" /> self-corrected{g.correction.resolved ? "" : " · still flagged"}
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-[#6E675F] mt-1 truncate">{g.category} · {g.references?.length || 0} refs · {g.faqs?.length || 0} FAQs · reviewed {g.last_reviewed}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {g.status === "published" && (
                      <Link to={`/guides/${g.slug}`} target="_blank" className="text-[#8C7B6B] hover:bg-[#EFEBE6] rounded-md p-2" title="View live" data-testid={`guide-view-${g.slug}`}><ExternalLink className="w-4 h-4" /></Link>
                    )}
                    <button onClick={() => setEditing(g)} className="text-[#8C7B6B] hover:bg-[#EFEBE6] rounded-md p-2" title="Edit" data-testid={`guide-edit-${g.slug}`}><Pencil className="w-4 h-4" /></button>
                    {flags > 0 && (
                      <button
                        disabled={busy === `fix-${g.id}`}
                        onClick={() => act(`fix-${g.id}`, async () => {
                          const { data: fixed } = await api.post(`/admin/guides/${g.id}/self-correct`);
                          const left = (fixed.dosing_flags?.length || 0) + (fixed.source_flags?.length || 0);
                          toast[left ? "warning" : "success"](left
                            ? `${g.peptide}: ${fixed.correction.rewritten_fields.length} passage(s) rewritten — ${left} flag(s) still need a human edit`
                            : `${g.peptide}: all flags resolved${fixed.status === "published" ? " — published" : ""}`);
                        })}
                        className="text-[#2D6A4F] hover:bg-[#EAF2EC] rounded-md p-2 disabled:opacity-50"
                        title="Run AI self-correction on the flagged passages"
                        data-testid={`guide-self-correct-${g.slug}`}
                      >
                        {busy === `fix-${g.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                      </button>
                    )}
                    {g.status === "published" ? (
                      <button disabled={busy === g.id} onClick={() => act(g.id, () => api.post(`/admin/guides/${g.id}/unpublish`), `${g.peptide} unpublished`)} className="text-[#8A6D2F] hover:bg-[#F6F1E7] rounded-md p-2 disabled:opacity-50" title="Unpublish" data-testid={`guide-unpublish-${g.slug}`}><EyeOff className="w-4 h-4" /></button>
                    ) : (
                      <button disabled={busy === g.id} onClick={() => act(g.id, () => api.post(`/admin/guides/${g.id}/publish`), `${g.peptide} published`)} className="text-[#2D6A4F] hover:bg-[#EAF2EC] rounded-md p-2 disabled:opacity-50" title="Publish" data-testid={`guide-publish-${g.slug}`}><Eye className="w-4 h-4" /></button>
                    )}
                    <button disabled={running} onClick={() => { if (window.confirm(`Regenerate ${g.peptide}? Your edits will be replaced.`)) generate({ peptides: [g.peptide], force: true }, `Regenerating ${g.peptide}…`); }} className="text-[#6E675F] hover:bg-[#EFEBE6] rounded-md p-2 disabled:opacity-50" title="Regenerate" data-testid={`guide-regenerate-${g.slug}`}><RotateCcw className="w-4 h-4" /></button>
                    <button disabled={busy === g.id} onClick={() => { if (window.confirm(`Delete the ${g.peptide} guide?`)) act(g.id, () => api.delete(`/admin/guides/${g.id}`), `${g.peptide} deleted`); }} className="text-[#B42318] hover:bg-[#FBEAEA] rounded-md p-2 disabled:opacity-50" title="Delete" data-testid={`guide-delete-${g.slug}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                {expanded === g.id && <div className="bg-[#FFFBF5] border-t border-[#F3E6CF]"><FlagList guide={g} /></div>}
              </div>
            );
          })}
        </div>
      )}

      <GuideEditDialog guide={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); onChanged?.(); }} />
    </div>
  );
};
